import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  ClipboardList,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Calendar,
  User,
  Users,
  Building2,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  ArrowRight,
  ChevronRight,
  FileText,
  Loader2,
  LayoutGrid,
  List,
  BarChart3,
  Trash2,
  Download,
  Kanban
} from 'lucide-react';
import TramitesKanban from '../components/TramitesKanban';
import TramitesCalendario from '../components/TramitesCalendario';

const Tramites = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [tramites, setTramites] = useState([]);
  const [config, setConfig] = useState(null);
  const [users, setUsers] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  
  // Vista
  const [viewMode, setViewMode] = useState('list'); // list, kanban, calendario
  const [activeTab, setActiveTab] = useState('todos');
  
  // Filtros
  const [filters, setFilters] = useState({
    estado: '',
    origen: '',
    prioridad: '',
    responsable_id: '',
    mis_tramites: false,
    alcance: 'todos',  // 'todos', 'mi_grupo', 'propio'
    grupo_id: ''
  });
  
  // Grupos para filtro
  const [grupos, setGrupos] = useState([]);
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Modal nuevo trámite
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [tipoFormulario, setTipoFormulario] = useState('normal'); // 'normal' o 'recurrente'
  const [newTramite, setNewTramite] = useState({
    titulo: '',
    descripcion: '',
    origen: 'INTERNO',
    tipo: 'REQUERIMIENTO_INFO',
    prioridad: 'MEDIA',
    fecha_vencimiento: '',
    responsable_id: '',
    asignados_ids: [],  // Otros asignados adicionales
    participantes_ids: [],
    supervisor_id: '',
    empresa_id: '',
    grupo_id: '',  // Grupo para permisos v2
    referencia_externa: '',
    dias_sin_actividad_escalar: 5,
    dias_antes_vencimiento_escalar: 3
  });
  // Campos para trámite recurrente
  const [newRecurrente, setNewRecurrente] = useState({
    titulo: '',
    descripcion: '',
    tipo_tramite: 'NOTIFICACION',
    prioridad: 'MEDIA',
    recurrencia: {
      tipo: 'MENSUAL',
      dia_semana: 0,
      dia_mes: 1,
      intervalo_dias: 30,
      fecha_inicio: new Date().toISOString().split('T')[0],
      fecha_fin: null,
      hora_notificacion: '09:00'
    },
    responsable_id: '',
    asignados_ids: [],
    supervisor_id: '',
    dias_anticipacion_notificar: 3,
    notificar_si_no_completado: true
  });
  const [recurrenteConfig, setRecurrenteConfig] = useState(null);
  const [creating, setCreating] = useState(false);

  // Abrir modal si viene de URL con ?new=true
  useEffect(() => {
    if (searchParams.get('new') === 'true') {
      setShowNewDialog(true);
      // Limpiar el parámetro de la URL
      searchParams.delete('new');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadTramites();
  }, [filters, activeTab]);

  const loadData = async () => {
    try {
      const [configRes, usersRes, empresasRes, dashboardRes, recConfigRes, gruposRes] = await Promise.all([
        api.get('/tramites/config').catch(e => { console.error('Error config:', e); return { data: null }; }),
        api.get('/users/asignables').catch(() => ({ data: [] })),
        api.get('/empresas').catch(() => ({ data: [] })),
        api.get('/tramites/dashboard').catch(e => { console.error('Error dashboard:', e); return { data: null }; }),
        api.get('/tramites/recurrentes/config/tipos').catch(() => ({ data: null })),
        api.get('/groups').catch(() => ({ data: [] }))
      ]);
      setConfig(configRes.data);
      setUsers(usersRes.data || []);
      setEmpresas(empresasRes.data || []);
      setDashboard(dashboardRes.data);
      setRecurrenteConfig(recConfigRes.data);
      setGrupos(gruposRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadTramites = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (activeTab === 'mis') {
        params.append('mis_tramites', 'true');
      } else if (activeTab === 'vencidos') {
        params.append('vencidos', 'true');
      } else if (activeTab === 'por_vencer') {
        params.append('por_vencer_dias', '7');
      }
      
      if (filters.estado) params.append('estado', filters.estado);
      if (filters.origen) params.append('origen', filters.origen);
      if (filters.prioridad) params.append('prioridad', filters.prioridad);
      if (filters.responsable_id) params.append('responsable_id', filters.responsable_id);
      
      // Filtro de alcance (permisos v2)
      if (filters.alcance && filters.alcance !== 'todos') {
        params.append('alcance', filters.alcance);
      }
      if (filters.grupo_id) {
        params.append('grupo_id', filters.grupo_id);
      }
      
      const res = await api.get(`/tramites?${params.toString()}`);
      setTramites(res.data);
    } catch (error) {
      console.error('Error loading tramites:', error);
      toast.error('Error al cargar trámites');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTramite = async () => {
    // Si es recurrente, usar la lógica de recurrentes
    if (tipoFormulario === 'recurrente') {
      await handleCreateRecurrente();
      return;
    }
    
    if (!newTramite.titulo || !newTramite.fecha_vencimiento || !newTramite.responsable_id) {
      toast.error('Complete los campos obligatorios');
      return;
    }
    
    setCreating(true);
    try {
      const res = await api.post('/tramites', newTramite);
      toast.success('Trámite creado correctamente');
      setShowNewDialog(false);
      resetFormulario();
      loadTramites();
      loadData(); // Refresh dashboard
    } catch (error) {
      toast.error('Error al crear trámite');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateRecurrente = async () => {
    if (!newRecurrente.titulo || !newRecurrente.responsable_id) {
      toast.error('Complete los campos obligatorios (título y responsable)');
      return;
    }
    
    setCreating(true);
    try {
      const payload = {
        ...newRecurrente,
        recurrencia: {
          ...newRecurrente.recurrencia,
          dia_semana: newRecurrente.recurrencia.tipo === 'SEMANAL' ? newRecurrente.recurrencia.dia_semana : null,
          dia_mes: ['MENSUAL', 'TRIMESTRAL', 'ANUAL'].includes(newRecurrente.recurrencia.tipo) ? newRecurrente.recurrencia.dia_mes : null,
          intervalo_dias: newRecurrente.recurrencia.tipo === 'PERSONALIZADO' ? newRecurrente.recurrencia.intervalo_dias : null
        }
      };
      
      const res = await api.post('/tramites/recurrentes', payload);
      toast.success(`Trámite recurrente creado. Próxima: ${new Date(res.data.proxima_ocurrencia).toLocaleDateString('es-ES')}`);
      setShowNewDialog(false);
      resetFormulario();
      loadData();
    } catch (error) {
      toast.error('Error al crear trámite recurrente');
    } finally {
      setCreating(false);
    }
  };

  const resetFormulario = () => {
    setTipoFormulario('normal');
    setNewTramite({
      titulo: '',
      descripcion: '',
      origen: 'INTERNO',
      tipo: 'REQUERIMIENTO_INFO',
      prioridad: 'MEDIA',
      fecha_vencimiento: '',
      responsable_id: '',
      asignados_ids: [],
      participantes_ids: [],
      supervisor_id: '',
      empresa_id: '',
      grupo_id: '',  // Reset grupo
      referencia_externa: '',
      dias_sin_actividad_escalar: 5,
      dias_antes_vencimiento_escalar: 3
    });
    setNewRecurrente({
      titulo: '',
      descripcion: '',
      tipo_tramite: 'NOTIFICACION',
      prioridad: 'MEDIA',
      recurrencia: {
        tipo: 'MENSUAL',
        dia_semana: 0,
        dia_mes: 1,
        intervalo_dias: 30,
        fecha_inicio: new Date().toISOString().split('T')[0],
        fecha_fin: null,
        hora_notificacion: '09:00'
      },
      responsable_id: '',
      asignados_ids: [],
      supervisor_id: '',
      dias_anticipacion_notificar: 3,
      notificar_si_no_completado: true
    });
  };

  const getOrigenColor = (origen) => {
    const o = config?.origenes?.find(x => x.id === origen);
    return o?.color || '#6b7280';
  };

  // Exportar a Excel
  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.estado) params.append('estado', filters.estado);
      if (filters.origen) params.append('origen', filters.origen);
      if (filters.prioridad) params.append('prioridad', filters.prioridad);
      if (filters.responsable_id) params.append('responsable_id', filters.responsable_id);
      
      const response = await api.get(`/tramites/export/excel?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tramites_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Archivo exportado correctamente');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar');
    }
  };

  // Eliminar trámite
  const handleDeleteTramite = async (tramiteId) => {
    setDeleting(true);
    try {
      await api.delete(`/tramites/${tramiteId}`);
      toast.success('Trámite eliminado');
      setDeleteConfirm(null);
      loadTramites();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar trámite');
    } finally {
      setDeleting(false);
    }
  };

  const getPrioridadColor = (prioridad) => {
    switch (prioridad) {
      case 'URGENTE': return 'bg-red-100 text-red-700 border-red-300';
      case 'ALTA': return 'bg-amber-100 text-amber-700 border-amber-300';
      case 'MEDIA': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'BAJA': return 'bg-slate-100 text-slate-600 border-slate-300';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const getEstadoConfig = (estado) => {
    switch (estado) {
      case 'NUEVO': return { color: 'bg-purple-100 text-purple-700', icon: Clock };
      case 'EN_PROCESO': return { color: 'bg-blue-100 text-blue-700', icon: RefreshCw };
      case 'EN_REVISION': return { color: 'bg-amber-100 text-amber-700', icon: Eye };
      case 'COMPLETADO': return { color: 'bg-green-100 text-green-700', icon: CheckCircle };
      case 'CANCELADO': return { color: 'bg-slate-100 text-slate-500', icon: XCircle };
      default: return { color: 'bg-slate-100 text-slate-600', icon: Clock };
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('es-ES');
    } catch {
      return dateStr;
    }
  };

  const clearFilters = () => {
    setFilters({
      estado: '',
      origen: '',
      prioridad: '',
      responsable_id: '',
      mis_tramites: false,
      alcance: 'todos',
      grupo_id: ''
    });
  };

  return (
    <div className="p-6 space-y-6" data-testid="tramites-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 pr-32">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <ClipboardList size={24} className="text-indigo-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Trámites y Tareas</h1>
            <p className="text-slate-600">Gestión de requerimientos regulatorios y tareas</p>
          </div>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => navigate('dashboard')}
            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
          >
            <BarChart3 size={18} className="mr-2" />
            Dashboard
          </Button>
          <Button
            onClick={() => setShowNewDialog(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="new-tramite-btn"
          >
            <Plus size={18} className="mr-2" />
            Nuevo Trámite
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="p-4 bg-white">
            <div className="text-2xl font-bold text-slate-900">{dashboard.totales?.activos || 0}</div>
            <div className="text-sm text-slate-500">Activos</div>
          </Card>
          <Card className="p-4 bg-white">
            <div className="text-2xl font-bold text-indigo-600">{dashboard.totales?.mis_pendientes || 0}</div>
            <div className="text-sm text-slate-500">Mis Pendientes</div>
          </Card>
          <Card className="p-4 bg-amber-50 border-amber-200">
            <div className="text-2xl font-bold text-amber-600">{dashboard.totales?.por_vencer || 0}</div>
            <div className="text-sm text-amber-700">Por Vencer</div>
          </Card>
          <Card className="p-4 bg-red-50 border-red-200">
            <div className="text-2xl font-bold text-red-600">{dashboard.totales?.vencidos || 0}</div>
            <div className="text-sm text-red-700">Vencidos</div>
          </Card>
          <Card className="p-4 bg-green-50 border-green-200">
            <div className="text-2xl font-bold text-green-600">{dashboard.totales?.completados || 0}</div>
            <div className="text-sm text-green-700">Completados</div>
          </Card>
          <Card className="p-4 bg-white">
            <div className="text-2xl font-bold text-slate-900">{dashboard.totales?.total || 0}</div>
            <div className="text-sm text-slate-500">Total</div>
          </Card>
        </div>
      )}

      {/* Tabs y Filtros */}
      <Card className="bg-white">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="border-b p-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <TabsList className="bg-slate-100">
                <TabsTrigger value="todos">Todos</TabsTrigger>
                <TabsTrigger value="mis">Mis Trámites</TabsTrigger>
                <TabsTrigger value="por_vencer" className="text-amber-700">Por Vencer</TabsTrigger>
                <TabsTrigger value="vencidos" className="text-red-700">Vencidos</TabsTrigger>
              </TabsList>
              
              <div className="flex items-center gap-2">
                <Button
                  variant={viewMode === 'list' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  title="Vista Lista"
                >
                  <List size={16} />
                </Button>
                <Button
                  variant={viewMode === 'kanban' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('kanban')}
                  title="Vista Kanban"
                >
                  <LayoutGrid size={16} />
                </Button>
                <Button
                  variant={viewMode === 'calendario' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('calendario')}
                  title="Vista Calendario"
                >
                  <Calendar size={16} />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleExportExcel}
                  title="Exportar a Excel"
                >
                  <Download size={16} />
                </Button>
                <Button variant="outline" size="sm" onClick={loadTramites}>
                  <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                </Button>
              </div>
            </div>
          </div>
          
          {/* Filtros */}
          <div className="p-4 bg-slate-50 border-b">
            <div className="flex flex-wrap items-end gap-3">
              {/* Filtro de Alcance */}
              <div className="w-40">
                <Label className="text-xs text-purple-700 font-medium">Alcance</Label>
                <Select value={filters.alcance || 'todos'} onValueChange={(v) => setFilters({ ...filters, alcance: v })}>
                  <SelectTrigger className="h-9 border-purple-200">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="mi_grupo">Mi Grupo</SelectItem>
                    <SelectItem value="grupos_supervisados">Mis Supervisados</SelectItem>
                    <SelectItem value="propio">Solo Míos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Filtro de Grupo */}
              {grupos.length > 0 && (
                <div className="w-44">
                  <Label className="text-xs text-purple-700 font-medium">Grupo</Label>
                  <Select value={filters.grupo_id || 'all'} onValueChange={(v) => setFilters({ ...filters, grupo_id: v === 'all' ? '' : v })}>
                    <SelectTrigger className="h-9 border-purple-200">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los grupos</SelectItem>
                      {grupos.map(g => (
                        <SelectItem key={g.id} value={g.id}>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: g.color || '#3b82f6' }} />
                            {g.nombre}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="w-40">
                <Label className="text-xs">Estado</Label>
                <Select value={filters.estado || 'all'} onValueChange={(v) => setFilters({ ...filters, estado: v === 'all' ? '' : v })}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="NUEVO">Nuevo</SelectItem>
                    <SelectItem value="EN_PROCESO">En Proceso</SelectItem>
                    <SelectItem value="EN_REVISION">En Revisión</SelectItem>
                    <SelectItem value="COMPLETADO">Completado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="w-40">
                <Label className="text-xs">Origen</Label>
                <Select value={filters.origen || 'all'} onValueChange={(v) => setFilters({ ...filters, origen: v === 'all' ? '' : v })}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {config?.origenes?.map(o => (
                      <SelectItem key={o.id} value={o.id}>{o.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="w-40">
                <Label className="text-xs">Prioridad</Label>
                <Select value={filters.prioridad || 'all'} onValueChange={(v) => setFilters({ ...filters, prioridad: v === 'all' ? '' : v })}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="URGENTE">Urgente</SelectItem>
                    <SelectItem value="ALTA">Alta</SelectItem>
                    <SelectItem value="MEDIA">Media</SelectItem>
                    <SelectItem value="BAJA">Baja</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="w-48">
                <Label className="text-xs">Responsable</Label>
                <Select value={filters.responsable_id || 'all'} onValueChange={(v) => setFilters({ ...filters, responsable_id: v === 'all' ? '' : v })}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Limpiar
              </Button>
            </div>
          </div>

          {/* Lista de Trámites */}
          <TabsContent value={activeTab} className="mt-0">
            {viewMode === 'list' ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Trámite</TableHead>
                      <TableHead>Origen</TableHead>
                      <TableHead>Estado</TableHead>
                      <TableHead>Prioridad</TableHead>
                      <TableHead>Responsable</TableHead>
                      <TableHead>Vencimiento</TableHead>
                      <TableHead className="w-20">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">
                          <Loader2 className="animate-spin mx-auto mb-2 text-slate-400" size={24} />
                          <p className="text-slate-500">Cargando trámites...</p>
                        </TableCell>
                      </TableRow>
                    ) : tramites.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                          No se encontraron trámites
                        </TableCell>
                      </TableRow>
                    ) : (
                      tramites.map(t => {
                        const EstadoIcon = getEstadoConfig(t.estado).icon;
                        return (
                          <TableRow 
                            key={t.id} 
                            className="hover:bg-slate-50 cursor-pointer"
                            onClick={() => navigate(`${t.id}`)}
                          >
                            <TableCell>
                              <div className="max-w-xs">
                                <p className="font-medium text-slate-900 truncate">{t.titulo}</p>
                                {t.referencia_externa && (
                                  <p className="text-xs text-slate-500">Ref: {t.referencia_externa}</p>
                                )}
                                {t.empresa && (
                                  <p className="text-xs text-slate-500 flex items-center gap-1">
                                    <Building2 size={12} />
                                    {t.empresa.razon_social}
                                  </p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                style={{ backgroundColor: getOrigenColor(t.origen) + '20', color: getOrigenColor(t.origen), borderColor: getOrigenColor(t.origen) }}
                                className="border"
                              >
                                {config?.origenes?.find(o => o.id === t.origen)?.nombre || t.origen}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={getEstadoConfig(t.estado).color}>
                                <EstadoIcon size={12} className="mr-1" />
                                {t.estado?.replace('_', ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={getPrioridadColor(t.prioridad)}>
                                {t.prioridad}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center">
                                  <User size={14} className="text-indigo-600" />
                                </div>
                                <div>
                                  <p className="text-sm">{t.responsable?.nombre || '-'}</p>
                                  {t.participantes?.length > 0 && (
                                    <p className="text-xs text-slate-500">+{t.participantes.length} participantes</p>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className={`text-sm ${t.dias_para_vencer < 0 ? 'text-red-600 font-medium' : t.dias_para_vencer <= 3 ? 'text-amber-600' : 'text-slate-600'}`}>
                                {formatDate(t.fecha_vencimiento)}
                                {t.dias_para_vencer !== undefined && (
                                  <p className="text-xs">
                                    {t.dias_para_vencer < 0 
                                      ? `Vencido hace ${Math.abs(t.dias_para_vencer)}d` 
                                      : t.dias_para_vencer === 0 
                                        ? 'Vence hoy' 
                                        : `${t.dias_para_vencer}d restantes`}
                                  </p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteConfirm(t);
                                  }}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 size={14} />
                                </Button>
                                <ChevronRight size={16} className="text-slate-400" />
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            ) : viewMode === 'kanban' ? (
              /* Kanban View Mejorado */
              <div className="p-4">
                <TramitesKanban 
                  onTramiteClick={(id) => navigate(`${id}`)}
                  filtros={filters}
                />
              </div>
            ) : (
              /* Calendario View */
              <div className="p-4">
                <TramitesCalendario 
                  onTramiteClick={(id) => navigate(`${id}`)}
                  filtros={filters}
                />
              </div>
            )}
          </TabsContent>
        </Tabs>
      </Card>

      {/* Dialog Confirmar Eliminación */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 size={20} />
              Eliminar Trámite
            </DialogTitle>
            <DialogDescription>
              ¿Está seguro que desea eliminar el trámite "{deleteConfirm?.titulo}"? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDeleteTramite(deleteConfirm?.id)}
              disabled={deleting}
            >
              {deleting ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Nuevo Trámite - Pantalla Completa */}
      <Dialog open={showNewDialog} onOpenChange={(open) => { setShowNewDialog(open); if (!open) resetFormulario(); }}>
        <DialogContent className="bg-white max-w-[95vw] w-full h-[95vh] max-h-[95vh] overflow-hidden flex flex-col">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="flex items-center gap-2 text-xl">
              <ClipboardList className="text-indigo-600" size={24} />
              Nuevo Trámite
            </DialogTitle>
            <DialogDescription>
              Seleccione el tipo de trámite y complete los datos
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-6 px-2">
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Selector de Tipo de Trámite */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <button
                  type="button"
                  onClick={() => setTipoFormulario('normal')}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    tipoFormulario === 'normal' 
                      ? 'border-indigo-500 bg-indigo-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${tipoFormulario === 'normal' ? 'bg-indigo-100' : 'bg-slate-100'}`}>
                      <ClipboardList size={24} className={tipoFormulario === 'normal' ? 'text-indigo-600' : 'text-slate-500'} />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Trámite Normal</p>
                      <p className="text-sm text-slate-500">Una sola ejecución con fecha de vencimiento</p>
                    </div>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => setTipoFormulario('recurrente')}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    tipoFormulario === 'recurrente' 
                      ? 'border-emerald-500 bg-emerald-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${tipoFormulario === 'recurrente' ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                      <Calendar size={24} className={tipoFormulario === 'recurrente' ? 'text-emerald-600' : 'text-slate-500'} />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Trámite Recurrente</p>
                      <p className="text-sm text-slate-500">Se repite automáticamente (diario, semanal, mensual...)</p>
                    </div>
                  </div>
                </button>
              </div>

              {/* Formulario Trámite Normal */}
              {tipoFormulario === 'normal' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <Label className="text-base font-medium">Título *</Label>
                    <Input
                      className="mt-1"
                      value={newTramite.titulo}
                      onChange={(e) => setNewTramite({ ...newTramite, titulo: e.target.value })}
                      placeholder="Ej: Requerimiento UIF - Información cliente XYZ"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-base font-medium">Origen *</Label>
                    <Select value={newTramite.origen} onValueChange={(v) => setNewTramite({ ...newTramite, origen: v })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {config?.origenes?.map(o => (
                          <SelectItem key={o.id} value={o.id}>{o.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-base font-medium">Tipo</Label>
                    <Select value={newTramite.tipo} onValueChange={(v) => setNewTramite({ ...newTramite, tipo: v })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {config?.tipos?.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-base font-medium">Prioridad</Label>
                    <Select value={newTramite.prioridad} onValueChange={(v) => setNewTramite({ ...newTramite, prioridad: v })}>
                    <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BAJA">Baja</SelectItem>
                        <SelectItem value="MEDIA">Media</SelectItem>
                        <SelectItem value="ALTA">Alta</SelectItem>
                        <SelectItem value="URGENTE">Urgente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-base font-medium">Fecha Vencimiento *</Label>
                    <Input
                      className="mt-1"
                      type="date"
                      value={newTramite.fecha_vencimiento}
                      onChange={(e) => setNewTramite({ ...newTramite, fecha_vencimiento: e.target.value })}
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <Label className="text-base font-medium">Descripción</Label>
                    <Textarea
                      className="mt-1"
                      value={newTramite.descripcion}
                      onChange={(e) => setNewTramite({ ...newTramite, descripcion: e.target.value })}
                      placeholder="Detalle del requerimiento..."
                      rows={3}
                    />
                  </div>
                  
                  <div>
                    <Label className="text-base font-medium">Responsable Principal *</Label>
                    <Select value={newTramite.responsable_id} onValueChange={(v) => setNewTramite({ ...newTramite, responsable_id: v })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-base font-medium">Supervisor (escalamiento)</Label>
                    <Select value={newTramite.supervisor_id || 'none'} onValueChange={(v) => setNewTramite({ ...newTramite, supervisor_id: v === 'none' ? '' : v })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Opcional..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin supervisor</SelectItem>
                        {users.filter(u => ['SUPERVISOR', 'ADMINISTRADOR', 'ADMIN'].includes(u.rol)).map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-base font-medium">Empresa relacionada</Label>
                    <Select value={newTramite.empresa_id || 'none'} onValueChange={(v) => setNewTramite({ ...newTramite, empresa_id: v === 'none' ? '' : v })}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Opcional..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin empresa</SelectItem>
                        {empresas.slice(0, 50).map(e => (
                          <SelectItem key={e.id} value={e.id}>{e.razon_social}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-base font-medium">Referencia Externa</Label>
                    <Input
                      className="mt-1"
                      value={newTramite.referencia_externa}
                      onChange={(e) => setNewTramite({ ...newTramite, referencia_externa: e.target.value })}
                      placeholder="Nro. expediente, nota, etc."
                    />
                  </div>
                  
                  {/* Selector de Grupo */}
                  {grupos.length > 0 && (
                    <div>
                      <Label className="text-base font-medium">Grupo</Label>
                      <Select value={newTramite.grupo_id || 'auto'} onValueChange={(v) => setNewTramite({ ...newTramite, grupo_id: v === 'auto' ? '' : v })}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Automático (mi grupo)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Automático (mi grupo)</SelectItem>
                          {grupos.map(g => (
                            <SelectItem key={g.id} value={g.id}>
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: g.color || '#3b82f6' }} />
                                {g.nombre}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500 mt-1">El grupo determina quién puede ver y editar este trámite</p>
                    </div>
                  )}
                </div>
              )}

              {/* Formulario Trámite Recurrente */}
              {tipoFormulario === 'recurrente' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <Label className="text-base font-medium">Título *</Label>
                      <Input
                        className="mt-1"
                        value={newRecurrente.titulo}
                        onChange={(e) => setNewRecurrente({ ...newRecurrente, titulo: e.target.value })}
                        placeholder="Ej: Presentación F8126 mensual"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <Label className="text-base font-medium">Descripción</Label>
                      <Textarea
                        className="mt-1"
                        value={newRecurrente.descripcion}
                        onChange={(e) => setNewRecurrente({ ...newRecurrente, descripcion: e.target.value })}
                        placeholder="Detalle de la tarea recurrente..."
                        rows={2}
                      />
                    </div>
                    
                    <div>
                      <Label className="text-base font-medium">Tipo de Trámite</Label>
                      <Select value={newRecurrente.tipo_tramite} onValueChange={(v) => setNewRecurrente({ ...newRecurrente, tipo_tramite: v })}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {recurrenteConfig?.tipos?.map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-base font-medium">Prioridad</Label>
                      <Select value={newRecurrente.prioridad} onValueChange={(v) => setNewRecurrente({ ...newRecurrente, prioridad: v })}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="BAJA">Baja</SelectItem>
                          <SelectItem value="MEDIA">Media</SelectItem>
                          <SelectItem value="ALTA">Alta</SelectItem>
                          <SelectItem value="URGENTE">Urgente</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Configuración de Recurrencia */}
                  <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                    <Label className="text-base font-medium text-emerald-900 mb-3 block">Configuración de Recurrencia</Label>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm">Frecuencia</Label>
                        <Select 
                          value={newRecurrente.recurrencia.tipo} 
                          onValueChange={(v) => setNewRecurrente({ 
                            ...newRecurrente, 
                            recurrencia: { ...newRecurrente.recurrencia, tipo: v }
                          })}
                        >
                          <SelectTrigger className="mt-1 bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {recurrenteConfig?.frecuencias?.map(f => (
                              <SelectItem key={f.id} value={f.id}>{f.nombre}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {newRecurrente.recurrencia.tipo === 'SEMANAL' && (
                        <div>
                          <Label className="text-sm">Día de la semana</Label>
                          <Select 
                            value={String(newRecurrente.recurrencia.dia_semana || 0)}
                            onValueChange={(v) => setNewRecurrente({
                              ...newRecurrente,
                              recurrencia: { ...newRecurrente.recurrencia, dia_semana: parseInt(v) }
                            })}
                          >
                            <SelectTrigger className="mt-1 bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {recurrenteConfig?.dias_semana?.map(d => (
                                <SelectItem key={d.id} value={String(d.id)}>{d.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      {['MENSUAL', 'TRIMESTRAL', 'ANUAL'].includes(newRecurrente.recurrencia.tipo) && (
                        <div>
                          <Label className="text-sm">Día del mes</Label>
                          <Input
                            type="number"
                            min={1}
                            max={31}
                            value={newRecurrente.recurrencia.dia_mes || 1}
                            onChange={(e) => setNewRecurrente({
                              ...newRecurrente,
                              recurrencia: { ...newRecurrente.recurrencia, dia_mes: parseInt(e.target.value) || 1 }
                            })}
                            className="mt-1 bg-white"
                          />
                        </div>
                      )}
                      
                      {newRecurrente.recurrencia.tipo === 'PERSONALIZADO' && (
                        <div>
                          <Label className="text-sm">Cada X días</Label>
                          <Input
                            type="number"
                            min={1}
                            value={newRecurrente.recurrencia.intervalo_dias || 30}
                            onChange={(e) => setNewRecurrente({
                              ...newRecurrente,
                              recurrencia: { ...newRecurrente.recurrencia, intervalo_dias: parseInt(e.target.value) || 30 }
                            })}
                            className="mt-1 bg-white"
                          />
                        </div>
                      )}
                      
                      <div>
                        <Label className="text-sm">Fecha de inicio</Label>
                        <Input
                          type="date"
                          value={newRecurrente.recurrencia.fecha_inicio}
                          onChange={(e) => setNewRecurrente({
                            ...newRecurrente,
                            recurrencia: { ...newRecurrente.recurrencia, fecha_inicio: e.target.value }
                          })}
                          className="mt-1 bg-white"
                        />
                      </div>
                      
                      <div>
                        <Label className="text-sm">Fecha de fin (opcional)</Label>
                        <Input
                          type="date"
                          value={newRecurrente.recurrencia.fecha_fin || ''}
                          onChange={(e) => setNewRecurrente({
                            ...newRecurrente,
                            recurrencia: { ...newRecurrente.recurrencia, fecha_fin: e.target.value || null }
                          })}
                          className="mt-1 bg-white"
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Asignación */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-base font-medium">Responsable Principal *</Label>
                      <Select value={newRecurrente.responsable_id} onValueChange={(v) => setNewRecurrente({ ...newRecurrente, responsable_id: v })}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent>
                          {users.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label className="text-base font-medium">Supervisor (para escalamiento)</Label>
                      <Select value={newRecurrente.supervisor_id || 'none'} onValueChange={(v) => setNewRecurrente({ ...newRecurrente, supervisor_id: v === 'none' ? '' : v })}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Opcional..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin supervisor</SelectItem>
                          {users.filter(u => ['SUPERVISOR', 'ADMINISTRADOR', 'ADMIN'].includes(u.rol)).map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Notificaciones */}
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <Label className="text-base font-medium mb-3 block">Configuración de Notificaciones</Label>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm">Notificar X días antes</Label>
                        <Input
                          type="number"
                          min={0}
                          value={newRecurrente.dias_anticipacion_notificar}
                          onChange={(e) => setNewRecurrente({ ...newRecurrente, dias_anticipacion_notificar: parseInt(e.target.value) || 3 })}
                          className="mt-1"
                        />
                      </div>
                      
                      <div className="flex items-center gap-2 mt-6">
                        <input
                          type="checkbox"
                          checked={newRecurrente.notificar_si_no_completado}
                          onChange={(e) => setNewRecurrente({ ...newRecurrente, notificar_si_no_completado: e.target.checked })}
                          className="rounded border-slate-300 text-emerald-600"
                        />
                        <Label className="text-sm font-normal">Recordar si no se completó</Label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => { setShowNewDialog(false); resetFormulario(); }}>
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateTramite} 
              disabled={creating} 
              className={tipoFormulario === "recurrente" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-indigo-600 hover:bg-indigo-700 text-white"}
            >
              {creating ? <Loader2 className="animate-spin mr-2" size={16} /> : <Plus size={16} className="mr-2" />}
              {tipoFormulario === 'recurrente' ? 'Crear Recurrente' : 'Crear Trámite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tramites;
